import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaCheck, FaChevronUp, FaExclamationTriangle, FaLink, FaLock, FaUserShield, FaSlidersH, FaSearch, FaTimes } from 'react-icons/fa';
import { Permissao } from '../../services/tipos';
import {
    AREAS_PERMISSAO,
    ModuloPermissao,
    NivelAcesso,
    PERFIS_USUARIO,
    PERMISSAO_OBRIGATORIA,
    PERMISSOES_FIXAS,
    ROTULO_NIVEL,
    completarDependencias,
    descricaoNivel,
    moduloCorresponde,
    niveisDoModulo,
} from '../../services/permissoes-config';

const AZUL = '#1F467E';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/* ---------- tipo de acesso ---------- */

const Tipos = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Tipo = styled.button<{ $ativo: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  padding: 9px 12px;
  border-radius: 10px;
  border: 2px solid ${p => (p.$ativo ? AZUL : '#e3e7ed')};
  background: ${p => (p.$ativo ? '#f2f6fc' : 'white')};
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: ${p => (p.$ativo ? AZUL : '#b8c4d6')};
  }

  &:disabled {
    cursor: default;
    opacity: ${p => (p.$ativo ? 1 : 0.55)};
  }
`;

const TipoMarca = styled.span<{ $ativo: boolean }>`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid ${p => (p.$ativo ? AZUL : '#adb5bd')};
  box-shadow: inset 0 0 0 3px white;
  background: ${p => (p.$ativo ? AZUL : 'white')};
`;

const TipoTitulo = styled.span`
  display: block;
  font-weight: 600;
  color: #212529;
  font-size: 0.9rem;
`;

const TipoTexto = styled.span`
  display: block;
  font-size: 0.76rem;
  color: #6c757d;
  margin-top: 1px;
`;

/* ---------- barra ---------- */

const Barra = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 10px;
`;

const Abas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Aba = styled.button<{ $ativa: boolean }>`
  border: 1px solid ${p => (p.$ativa ? AZUL : '#dee2e6')};
  background: ${p => (p.$ativa ? AZUL : 'white')};
  color: ${p => (p.$ativa ? 'white' : '#495057')};
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.82rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: ${AZUL};
  }
`;

const Contagem = styled.span<{ $ativa: boolean }>`
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0 6px;
  background: ${p => (p.$ativa ? 'rgba(255,255,255,0.22)' : '#eef2f7')};
  color: ${p => (p.$ativa ? 'white' : '#6c757d')};
`;

const Acoes = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Modelo = styled.select`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.82rem;
  color: #495057;
  background: white;
  max-width: 190px;
`;

const BotaoTexto = styled.button<{ $perigo?: boolean }>`
  border: none;
  background: none;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 500;
  color: ${p => (p.$perigo ? '#c82333' : AZUL)};

  &:hover {
    background: ${p => (p.$perigo ? '#fff5f5' : '#eef3fa')};
  }
`;

const Dica = styled.div`
  font-size: 0.78rem;
  color: #6c757d;
`;

const Alerta = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 0.82rem;
  color: #6b4e00;
  background: #fff8e1;
  border: 1px solid #ffe08a;
  border-radius: 8px;
  padding: 8px 12px;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const PainelAdmin = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  border-radius: 10px;
  background: #f2f6fc;
  color: #343a40;
  font-size: 0.9rem;

  svg {
    color: ${AZUL};
    flex-shrink: 0;
  }
`;

/* ---------- módulos ---------- */

const Lista = styled.div`
  display: flex;
  flex-direction: column;
`;

const Linha = styled.div`
  padding: 10px 4px;
  border-bottom: 1px solid #f1f3f5;

  &:last-child {
    border-bottom: none;
  }
`;

const LinhaTopo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
`;

const ModuloTexto = styled.div`
  flex: 1 1 220px;
  min-width: 0;
`;

const ModuloNome = styled.div<{ $ativo: boolean }>`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${p => (p.$ativo ? '#212529' : '#6c757d')};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModuloDescricao = styled.div`
  font-size: 0.76rem;
  color: #8a939c;
  line-height: 1.3;
`;

const Segmentos = styled.div`
  display: inline-flex;
  background: #f1f3f5;
  border-radius: 8px;
  padding: 2px;
  max-width: 100%;
  flex-wrap: wrap;
`;

const Segmento = styled.button<{ $ativo: boolean; $nivel: string }>`
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.78rem;
  font-weight: ${p => (p.$ativo ? 600 : 500)};
  white-space: nowrap;
  background: ${p => (!p.$ativo ? 'transparent' : p.$nivel === 'nenhum' ? 'white' : AZUL)};
  color: ${p => (!p.$ativo ? '#6c757d' : p.$nivel === 'nenhum' ? '#495057' : 'white')};
  box-shadow: ${p => (p.$ativo ? '0 1px 2px rgba(0,0,0,0.12)' : 'none')};

  &:hover:not(:disabled) {
    color: ${p => (p.$ativo ? undefined : '#212529')};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${p => (p.$ativo ? undefined : '#c4cad0')};
  }
`;

const BotaoAjustar = styled.button<{ $aberto: boolean }>`
  border: 1px solid ${p => (p.$aberto ? AZUL : 'transparent')};
  background: ${p => (p.$aberto ? '#eef3fa' : 'transparent')};
  color: ${p => (p.$aberto ? AZUL : '#8a939c')};
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${AZUL};
    background: #eef3fa;
  }
`;

const Selo = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: #7a5b00;
  background: #fff3cd;
  border-radius: 999px;
  padding: 1px 7px;
`;

const Itens = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Item = styled.button<{ $marcado: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${p => (p.$marcado ? AZUL : '#dee2e6')};
  background: ${p => (p.$marcado ? '#eef3fa' : 'white')};
  color: ${p => (p.$marcado ? AZUL : '#495057')};
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 0.78rem;
  font-weight: 500;

  svg {
    font-size: 9px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }
`;

const Detalhe = styled.div`
  margin-top: 5px;
  font-size: 0.74rem;
  color: #8a939c;
  display: flex;
  gap: 6px;
  align-items: flex-start;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Busca = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg.lupa {
    position: absolute;
    left: 12px;
    color: #adb5bd;
  }

  input {
    width: 100%;
    border: 1px solid #ced4da;
    border-radius: 8px;
    padding: 8px 36px;
    font-size: 0.9rem;
    color: #212529;
  }

  input:focus {
    outline: none;
    border-color: ${AZUL};
    box-shadow: 0 0 0 2px rgba(31, 70, 126, 0.15);
  }

  button {
    position: absolute;
    right: 8px;
    border: none;
    background: transparent;
    color: #6c757d;
    padding: 4px;
    display: flex;
  }
`;

const TituloDaArea = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin: 6px 0 -2px;
`;

const Vazio = styled.div`
  text-align: center;
  padding: 32px;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 8px;
`;

interface GerenciadorPermissoesProps {
    permissoesDisponiveis: Permissao[];
    permissoesSelecionadas: Permissao[] | '' | undefined;
    onChange: (novasPermissoes: Permissao[]) => void;
    /** Formulário só de visualização: mostra as permissões sem deixar alterar. */
    somenteLeitura?: boolean;
    /** Administrador tem acesso a tudo: a escolha por módulo fica oculta. */
    administrador?: boolean;
    /** Troca entre administrador e acesso por módulo; sem ela, o tipo de acesso só é exibido. */
    onAdministradorChange?: (administrador: boolean) => void;
    /** Requisitos diretos de cada chave (GET /Permissao/Dependencias). */
    dependencias: Record<string, string[]>;
    /** Empresa da plataforma: exibe também os módulos internos (cupons). */
    ehPlataforma?: boolean;
}

type ModuloVisivel = ModuloPermissao & { chaves: string[] };

const achatar = <T,>(listas: T[][]): T[] => listas.reduce<T[]>((acc, l) => acc.concat(l), []);
const todosOsModulos = (): ModuloPermissao[] => achatar(AREAS_PERMISSAO.map(a => a.modulos));

/** Chaves que o cadastro de usuários exibe para esta empresa (usado também no salvar do administrador). */
export function chavesExibidas(permissoesDisponiveis: Permissao[], ehPlataforma: boolean): string[] {
    return achatar(montarModulos(permissoesDisponiveis, ehPlataforma).map(m => m.chaves));
}

function montarModulos(permissoesDisponiveis: Permissao[], ehPlataforma: boolean): ModuloVisivel[] {
    const existentes = new Set(permissoesDisponiveis.map(p => p.chave));
    const conhecidas = new Set(achatar(todosOsModulos().map(m => m.acoes.map(x => x.chave))));

    const modulos: ModuloVisivel[] = todosOsModulos()
        .filter(m => ehPlataforma || !m.somentePlataforma)
        .map(m => {
            const acoes = m.acoes.filter(a => existentes.has(a.chave));
            return { ...m, acoes, chaves: acoes.map(a => a.chave) };
        })
        .filter(m => m.acoes.length > 0);

    // Permissões novas que ainda não entraram no catálogo da tela continuam disponíveis aqui.
    const outras = permissoesDisponiveis
        .filter(p => !conhecidas.has(p.chave) && p.chave !== PERMISSAO_OBRIGATORIA && !PERMISSOES_FIXAS.includes(p.chave))
        .map(p => ({ chave: p.chave, rotulo: `${p.grupo}: ${p.nome}` }));
    if (outras.length > 0) {
        modulos.push({
            id: 'outras', nome: 'Outras permissões', descricao: 'Permissões sem módulo próprio nesta tela.',
            somenteItens: true, acoes: outras, chaves: outras.map(o => o.chave),
        });
    }
    return modulos;
}

export const GerenciadorPermissoes: React.FC<GerenciadorPermissoesProps> = ({
    permissoesDisponiveis,
    permissoesSelecionadas,
    onChange,
    somenteLeitura = false,
    administrador = false,
    onAdministradorChange,
    dependencias,
    ehPlataforma = false,
}) => {
    const [modelo, setModelo] = useState('');
    const [abertos, setAbertos] = useState<Set<string>>(new Set());
    const [areaAtiva, setAreaAtiva] = useState<string>(AREAS_PERMISSAO[0].nome);
    const [busca, setBusca] = useState('');
    const travado = somenteLeitura || administrador;

    const catalogo = useMemo(() => new Map(permissoesDisponiveis.map(p => [p.chave, p])), [permissoesDisponiveis]);
    const modulos = useMemo(() => montarModulos(permissoesDisponiveis, ehPlataforma), [permissoesDisponiveis, ehPlataforma]);
    const todasExibidas = useMemo(() => new Set(achatar(modulos.map(m => m.chaves))), [modulos]);
    const moduloDaChave = useMemo(() => {
        const mapa = new Map<string, ModuloVisivel>();
        modulos.forEach(m => m.chaves.forEach(c => mapa.set(c, m)));
        return mapa;
    }, [modulos]);
    const rotuloDaChave = (chave: string) => {
        const modulo = moduloDaChave.get(chave);
        const acao = modulo?.acoes.find(a => a.chave === chave);
        return modulo ? `${modulo.nome} (${acao?.rotulo ?? chave})` : (catalogo.get(chave)?.nome ?? chave);
    };

    const originais = useMemo(
        () => (Array.isArray(permissoesSelecionadas) ? permissoesSelecionadas : []),
        [permissoesSelecionadas]
    );
    const selecionadas = useMemo(() => new Set(originais.map(p => p.chave)), [originais]);
    const efetivas = selecionadas;
    const fechar = (chaves: Iterable<string>) => completarDependencias(chaves, dependencias);

    const faltantes = useMemo(() => {
        if (administrador) return [];
        const fechadas = completarDependencias(selecionadas, dependencias);
        return Array.from(fechadas).filter(c => !selecionadas.has(c) && catalogo.has(c));
    }, [administrador, selecionadas, dependencias, catalogo]);

    const emitir = (chaves: Set<string>) => {
        if (travado) return;
        const porChave = new Map(originais.map(p => [p.chave, p]));
        const resultado: Permissao[] = [];
        chaves.forEach(c => {
            const permissao = catalogo.get(c) ?? porChave.get(c);
            if (permissao) resultado.push(permissao);
        });
        const obrigatoria = catalogo.get(PERMISSAO_OBRIGATORIA);
        if (obrigatoria && !chaves.has(PERMISSAO_OBRIGATORIA)) resultado.push(obrigatoria);
        onChange(resultado);
    };

    const semModulo = (modulo: ModuloVisivel) => new Set(Array.from(efetivas).filter(c => !modulo.chaves.includes(c)));

    /** Chaves do módulo que outras permissões marcadas exigem, com os módulos que exigem. */
    const exigenciasDe = (modulo: ModuloVisivel) => {
        const resto = semModulo(modulo);
        const exigidas = new Set(Array.from(fechar(resto)).filter(c => modulo.chaves.includes(c)));
        const motivo = (faltando: string[]) => {
            const nomes = new Set<string>();
            resto.forEach(c => {
                const fecho = fechar([c]);
                if (faltando.some(f => fecho.has(f))) {
                    const m = moduloDaChave.get(c);
                    if (m) nomes.add(m.nome);
                }
            });
            return Array.from(nomes).join(', ');
        };
        return { exigidas, motivo };
    };

    const definirNivel = (modulo: ModuloVisivel, chavesNivel: string[]) => {
        setModelo('');
        emitir(fechar([...Array.from(semModulo(modulo)), ...chavesNivel]));
    };

    const alternarItem = (chave: string) => {
        setModelo('');
        if (efetivas.has(chave)) {
            const novas = new Set(efetivas);
            novas.delete(chave);
            emitir(novas);
        } else {
            emitir(fechar([...Array.from(efetivas), chave]));
        }
    };

    const aplicarModelo = (valor: string) => {
        setModelo(valor);
        const perfil = PERFIS_USUARIO.find(p => p.valor === valor);
        if (!perfil) return;
        const fora = Array.from(efetivas).filter(c => !todasExibidas.has(c));
        emitir(fechar([...fora, ...perfil.permissoes.filter(c => catalogo.has(c))]));
    };

    const liberarTudo = () => {
        setModelo('');
        emitir(new Set([...Array.from(efetivas), ...Array.from(todasExibidas)]));
    };

    const limpar = () => {
        setModelo('');
        emitir(new Set(Array.from(efetivas).filter(c => !todasExibidas.has(c))));
    };

    const alternarAberto = (id: string) => {
        setAbertos(prev => {
            const novo = new Set(prev);
            if (novo.has(id)) novo.delete(id); else novo.add(id);
            return novo;
        });
    };

    if (modulos.length === 0) {
        return <Vazio>Nenhuma permissão disponível para exibição.</Vazio>;
    }

    const podeTrocarTipo = !!onAdministradorChange && !somenteLeitura;
    const seletorDeTipo = (
        <Tipos role="radiogroup" aria-label="Tipo de acesso">
            <Tipo
                type="button" role="radio" aria-checked={!administrador} $ativo={!administrador}
                disabled={!podeTrocarTipo} onClick={() => onAdministradorChange?.(false)}
            >
                <TipoMarca $ativo={!administrador} />
                <span>
                    <TipoTitulo>Por módulo</TipoTitulo>
                    <TipoTexto>Escolha o que o usuário pode ver e fazer em cada área.</TipoTexto>
                </span>
            </Tipo>
            <Tipo
                type="button" role="radio" aria-checked={administrador} $ativo={administrador}
                disabled={!podeTrocarTipo} onClick={() => onAdministradorChange?.(true)}
                title={!onAdministradorChange && !somenteLeitura ? 'Só um administrador pode conceder acesso de administrador.' : undefined}
            >
                <TipoMarca $ativo={administrador} />
                <span>
                    <TipoTitulo>Administrador</TipoTitulo>
                    <TipoTexto>Acesso total, inclusive a módulos que forem criados.</TipoTexto>
                </span>
            </Tipo>
        </Tipos>
    );

    if (administrador) {
        return (
            <Container>
                {seletorDeTipo}
                <PainelAdmin>
                    <FaUserShield size={22} />
                    <span>
                        Este usuário terá acesso a todos os módulos e configurações.
                        {!somenteLeitura && ' Para limitar o acesso, escolha "Por módulo".'}
                    </span>
                </PainelAdmin>
            </Container>
        );
    }

    const areas = [
        ...AREAS_PERMISSAO.map(a => ({ nome: a.nome, modulos: modulos.filter(m => a.modulos.some(x => x.id === m.id)) })),
        { nome: 'Outras', modulos: modulos.filter(m => m.id === 'outras') },
    ].filter(a => a.modulos.length > 0);
    const area = areas.find(a => a.nome === areaAtiva) ?? areas[0];
    const buscando = busca.trim().length > 0;
    const encontrados = buscando
        ? areas.map(a => ({ nome: a.nome, modulos: a.modulos.filter(m => moduloCorresponde(m, busca)) })).filter(a => a.modulos.length > 0)
        : [];
    const comAcesso = (m: ModuloVisivel) => m.chaves.some(c => efetivas.has(c));

    const renderModulo = (modulo: ModuloVisivel) => {
        const marcadas = modulo.chaves.filter(c => efetivas.has(c));
        const niveis = niveisDoModulo(modulo.acoes);
        const atual = modulo.somenteItens
            ? undefined
            : niveis.find(n => n.chaves.length === marcadas.length && n.chaves.every(c => efetivas.has(c)))?.nivel;
        const personalizado = !modulo.somenteItens && !atual;
        const aberto = modulo.somenteItens || personalizado || abertos.has(modulo.id);
        const { exigidas, motivo } = exigenciasDe(modulo);
        const incluidas = Array.from(fechar(marcadas)).filter(c => !modulo.chaves.includes(c) && catalogo.has(c));
        const exigidoPor = exigidas.size > 0 ? motivo(Array.from(exigidas)) : '';

        return (
            <Linha key={modulo.id}>
                <LinhaTopo>
                    <ModuloTexto>
                        <ModuloNome $ativo={marcadas.length > 0}>
                            {modulo.nome}
                            {personalizado && <Selo>Personalizado</Selo>}
                            {exigidoPor && !somenteLeitura && (
                                <FaLock size={10} color="#adb5bd" title={`Exigido por ${exigidoPor}`} />
                            )}
                        </ModuloNome>
                        <ModuloDescricao>{modulo.descricao}</ModuloDescricao>
                    </ModuloTexto>

                    {!modulo.somenteItens && (
                        <Segmentos role="group" aria-label={`Acesso a ${modulo.nome}`}>
                            {niveis.map(n => {
                                const faltando = Array.from(exigidas).filter(c => !n.chaves.includes(c));
                                const bloqueado = faltando.length > 0;
                                const ativo = atual === n.nivel;
                                return (
                                    <Segmento
                                        key={n.nivel}
                                        type="button"
                                        $ativo={ativo}
                                        $nivel={n.nivel}
                                        aria-pressed={ativo}
                                        disabled={travado || bloqueado}
                                        title={bloqueado && !travado
                                            ? `Exigido por ${motivo(faltando)}`
                                            : descricaoNivel(n.nivel as NivelAcesso)}
                                        onClick={() => definirNivel(modulo, n.chaves)}
                                    >
                                        {ROTULO_NIVEL[n.nivel]}
                                    </Segmento>
                                );
                            })}
                        </Segmentos>
                    )}

                    {!modulo.somenteItens && !personalizado && !somenteLeitura && (
                        <BotaoAjustar
                            type="button"
                            $aberto={abertos.has(modulo.id)}
                            title={abertos.has(modulo.id) ? 'Fechar ações' : 'Escolher ação por ação'}
                            aria-label={`Escolher ações de ${modulo.nome}`}
                            onClick={() => alternarAberto(modulo.id)}
                        >
                            {abertos.has(modulo.id) ? <FaChevronUp size={11} /> : <FaSlidersH size={12} />}
                        </BotaoAjustar>
                    )}
                </LinhaTopo>

                {aberto && (
                    <Itens>
                        {modulo.acoes.map(a => {
                            const marcado = efetivas.has(a.chave);
                            const exigido = marcado && exigidas.has(a.chave);
                            return (
                                <Item
                                    key={a.chave}
                                    type="button"
                                    $marcado={marcado}
                                    aria-pressed={marcado}
                                    disabled={travado || exigido}
                                    title={exigido ? `Exigido por ${motivo([a.chave])}` : undefined}
                                    onClick={() => alternarItem(a.chave)}
                                >
                                    {marcado && <FaCheck />} {a.rotulo}
                                </Item>
                            );
                        })}
                    </Itens>
                )}

                {modulo.tambemLibera && marcadas.length > 0 && (
                    <Detalhe><FaExclamationTriangle /> Também libera, {modulo.tambemLibera}</Detalhe>
                )}
                {incluidas.length > 0 && (
                    <Detalhe><FaLink /> Inclui {incluidas.map(rotuloDaChave).join(', ')}</Detalhe>
                )}
            </Linha>
        );
    };

    return (
        <Container>
            {seletorDeTipo}

            {faltantes.length > 0 && (
                <Alerta>
                    <FaExclamationTriangle />
                    <span>
                        Faltam permissões que algumas telas precisam para carregar: {faltantes.map(rotuloDaChave).join(', ')}.
                        {!somenteLeitura && ' Elas serão incluídas ao salvar.'}
                    </span>
                </Alerta>
            )}

            <Busca>
                <FaSearch className="lupa" size={13} />
                <input
                    type="search"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Pesquisar permissão (ex.: bloquear, nota fiscal, multas)"
                    aria-label="Pesquisar permissão"
                />
                {buscando && (
                    <button type="button" onClick={() => setBusca('')} aria-label="Limpar pesquisa"><FaTimes size={12} /></button>
                )}
            </Busca>

            <Barra>
                <Abas role="tablist">
                    {!buscando && areas.map(a => {
                        const ativa = a.nome === area.nome;
                        return (
                            <Aba key={a.nome} type="button" role="tab" aria-selected={ativa} $ativa={ativa} onClick={() => setAreaAtiva(a.nome)}>
                                {a.nome}
                                <Contagem $ativa={ativa}>{a.modulos.filter(comAcesso).length}/{a.modulos.length}</Contagem>
                            </Aba>
                        );
                    })}
                </Abas>
                {!somenteLeitura && (
                    <Acoes>
                        <Modelo value={modelo} onChange={e => aplicarModelo(e.target.value)} aria-label="Começar de um modelo">
                            <option value="">Usar um modelo...</option>
                            {PERFIS_USUARIO.map(p => <option key={p.valor} value={p.valor}>{p.label}</option>)}
                        </Modelo>
                        <BotaoTexto type="button" onClick={liberarTudo}>Liberar tudo</BotaoTexto>
                        <BotaoTexto type="button" $perigo onClick={limpar}>Limpar</BotaoTexto>
                    </Acoes>
                )}
            </Barra>

            {!somenteLeitura && (
                <Dica>O que cada tela precisa para abrir entra junto. As mudanças valem no próximo login do usuário.</Dica>
            )}

            {buscando ? (
                <Lista aria-label="Resultado da pesquisa">
                    {encontrados.length === 0 ? (
                        <Vazio>Nenhuma permissão encontrada para "{busca.trim()}".</Vazio>
                    ) : encontrados.map(a => (
                        <React.Fragment key={a.nome}>
                            <TituloDaArea>{a.nome}</TituloDaArea>
                            {a.modulos.map(renderModulo)}
                        </React.Fragment>
                    ))}
                </Lista>
            ) : (
                <Lista role="tabpanel" aria-label={area.nome}>
                    {area.modulos.map(renderModulo)}
                </Lista>
            )}
        </Container>
    );
};

